import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Zap, Search, LayoutDashboard, LogOut, Menu, X, ChevronDown, MessageSquare, LifeBuoy, Crown } from 'lucide-react';
import NotificationBell from '@/components/NotificationBell';
import { useApp } from '@/context/AppContext';

const Navbar: React.FC = () => {
  const { userProfile, logout } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const dashboardPath = userProfile?.role === 'super_admin' ? '/admin' : '/dashboard';
  const initials = userProfile?.name?.slice(0, 2).toUpperCase() ?? '';

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'glass-dark border-b border-white/5 py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="relative">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center shadow-glow-violet group-hover:scale-110 transition-transform duration-300">
                <Zap size={16} className="text-white fill-white" />
              </div>
              <div className="absolute -inset-1 rounded-xl bg-gradient-to-br from-violet-500 to-purple-700 opacity-0 group-hover:opacity-20 blur-lg transition-opacity duration-300" />
            </div>
            <span className="font-display font-bold text-xl tracking-tight">
              <span className="text-white">Bachelor</span>
              <span className="gradient-text">Pad</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/search" icon={<Search size={15} />} label="Find Home" active={location.pathname === '/search'} />
            <NavLink to="/pricing" icon={<Crown size={15} />} label="Pricing" active={location.pathname === '/pricing'} />
            {userProfile && (
              <NavLink to={dashboardPath} icon={<LayoutDashboard size={15} />} label="Dashboard" active={location.pathname.startsWith('/dashboard') || location.pathname === '/admin'} />
            )}
            {userProfile && userProfile.role !== 'admin' && userProfile.role !== 'super_admin' && (
              <>
                <NavLink to="/messages" icon={<MessageSquare size={15} />} label="Messages" active={location.pathname === '/messages'} />
                <NavLink to="/support" icon={<LifeBuoy size={15} />} label="Support" active={location.pathname === '/support'} />
              </>
            )}
          </nav>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            {userProfile ? (
              <div className="flex items-center gap-3">
                <NotificationBell />
                <div className="flex items-center gap-2 glass-light px-3 py-1.5 rounded-full cursor-default">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-xs font-bold text-white">
                    {initials}
                  </div>
                  <span className="text-sm text-white/80 font-medium max-w-[120px] truncate">{userProfile.name.split(' ')[0]}</span>
                  <ChevronDown size={12} className="text-white/40" />
                </div>
                <button
                  onClick={() => { logout(); navigate('/'); }}
                  className="flex items-center gap-2 text-sm text-slate-400 hover:text-white glass-light px-3 py-1.5 rounded-full transition-all hover:border-white/20 group"
                >
                  <LogOut size={14} className="group-hover:text-red-400 transition-colors" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" className="text-sm text-slate-300 hover:text-white px-4 py-2 rounded-full transition-colors font-medium">
                  Login
                </Link>
                <Link to="/login" className="relative group">
                  <span className="relative z-10 flex items-center gap-1.5 text-sm font-bold text-white bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 px-5 py-2.5 rounded-full transition-all duration-300 shadow-glow-violet">
                    Get Started
                    <Zap size={13} className="fill-white" />
                  </span>
                  <span className="absolute inset-0 rounded-full bg-gradient-to-r from-violet-600 to-purple-600 blur-md opacity-40 group-hover:opacity-60 transition-opacity" />
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Burger */}
          <button
            className="md:hidden glass-light p-2 rounded-xl"
            onClick={() => setMobileOpen(v => !v)}
          >
            {mobileOpen ? <X size={20} className="text-white" /> : <Menu size={20} className="text-white" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden glass-dark border-t border-white/5 mt-3 animate-slide-down">
          <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
            <MobileNavLink to="/search" label="Find Home" icon={<Search size={16} />} />
            {userProfile && (
              <MobileNavLink to={dashboardPath} label="Dashboard" icon={<LayoutDashboard size={16} />} />
            )}
            {userProfile ? (
              <button
                onClick={() => { logout(); navigate('/'); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 font-medium transition-colors text-left"
              >
                <LogOut size={16} /> Logout
              </button>
            ) : (
              <Link
                to="/login"
                className="flex items-center justify-center gap-2 w-full mt-2 text-white font-bold bg-gradient-to-r from-violet-600 to-purple-600 px-5 py-3 rounded-xl"
              >
                <Zap size={15} className="fill-white" /> Get Started
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

const NavLink: React.FC<{ to: string; icon: React.ReactNode; label: string; active: boolean }> = ({ to, icon, label, active }) => (
  <Link
    to={to}
    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
      active
        ? 'bg-violet-500/15 text-violet-300 border border-violet-500/20'
        : 'text-slate-400 hover:text-white hover:bg-white/5'
    }`}
  >
    {icon}
    {label}
  </Link>
);

const MobileNavLink: React.FC<{ to: string; label: string; icon: React.ReactNode }> = ({ to, label, icon }) => (
  <Link
    to={to}
    className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 font-medium transition-colors"
  >
    {icon} {label}
  </Link>
);

export default Navbar;
